/**
 * soundtrack-splice.js — window.SoundtrackSplice
 *
 * A tool that changes ONLY the sound of a video renders the new soundtrack
 * with its own FFmpeg graph, and this puts it into the ORIGINAL file: the
 * picture stays file.slice() references (MP4TrackRemux.replaceAudio), not one
 * video byte goes through FFmpeg or the JS heap.
 *
 * WHY (29.09.2026). The classic export of these tools was one FFmpeg run with
 * `-c:v copy`: every byte of the picture crossed into the wasm heap and the
 * whole OUTPUT was built in MEMFS — the 1600 MiB wall (layout
 * __WASM_OUTPUT_MAX). So their zones could not promise more than 1 GiB, and
 * Premium's «до 4 ч» met «максимум 1024 МБ» on an ordinary phone video
 * (Premium chats 657 and 659: 3.0 GB / 26:49 1080p120 and 2.5 GB). Measured
 * on the real /profanity-remover-video page, Edge 154, a 3.2 GB file of that
 * shape: the copy export died at 60 % (RangeError, 4.8 GB peak); the splice
 * inserted the soundtrack in 1.04 s at a 1.6 GB peak, picture byte-identical,
 * Media Foundation decoding every second of the sound. First owner:
 * /profanity-remover-video; docs/why/primitives/video-audio-lane.md.
 *
 * What the helpers encode (each one measured, not reasoned):
 *   · OUT_NAME is `.mp4`, never `.m4a`: the processor clamps an AUDIO output's
 *     bitrate to the plan's quality ceiling by its extension
 *     (_clampAudioKbpsArgs), and a video's soundtrack is a passenger —
 *     measured as `.m4a`: 114 kb/s, −22.7 dB from the source, against 172 kb/s
 *     and −34.0 dB for the copy export.
 *   · alignGraph / alignChain put the video's own sound on the timeline it was
 *     analysed on: `aresample=async=1:first_pts=0` on every [0:a]. A track that
 *     starts after the picture then comes out padded with real silence — not
 *     as an m4a opening with an empty edit, which Media Foundation ignores
 *     (the copy export played such files 1.4 s early on Windows; measured).
 *   · leadFilter moves the soundtrack by FFmpeg's «start:» of the file (FFmpeg
 *     renders from the input's start_time, the movie counts from 0) — IN THE
 *     SAMPLES, never as an edit-list offset (same reason).
 *   · capSec: the copy export ran `-shortest`; a rendered soundtrack longer
 *     than the picture (a 10-min song on a 3-min clip) would stretch the movie
 *     with a frozen last frame. Callers pass the video's duration.
 *
 *   SoundtrackSplice.capable(file)                 → bool (mp4-family + the remux module)
 *   SoundtrackSplice.OUT_NAME                      → 'soundtrack.mp4'
 *   SoundtrackSplice.leadFilter(startSec, rate)    → '' | 'adelay=…S:all=1' | 'atrim=…'
 *   SoundtrackSplice.alignGraph(graph, {rate, startSec})   filter_complex ending in [outa]
 *   SoundtrackSplice.alignChain(chain, {rate, startSec})   an -af chain on the video's sound
 *   SoundtrackSplice.probe(file)                   → Promise<{startSec, rate, channels}> (FFmpeg's probe, cached)
 *   SoundtrackSplice.startOf(file)                 → Promise<seconds> («start:» of that probe)
 *   SoundtrackSplice.splice(file, soundtrackBlob, {mime}) → Promise<{blob, info}>
 *        throws the remux refusal (.remuxReason) or the browser's read error
 *   SoundtrackSplice.spliceDecoded(file, wav, {P, kbps, mime, udta, race}) → Promise<{blob, info}>
 *        a WAV built on the video's DECODED sound, put back where the movie plays it
 *   SoundtrackSplice.shiftDecoded(file, wav, P)   → Promise<{wav, lead_ms}> (the same, for a copy mux)
 *   SoundtrackSplice.copyWallBytes()               → the MEMFS output wall (layout's number)
 *   SoundtrackSplice.classicCeiling()              → the biggest file the classic copy export
 *        is still asked to take: min(the wall, the device's 'video' kind)
 */
(function (w) {
    'use strict';
    if (w.SoundtrackSplice) return;

    var OUT_NAME = 'soundtrack.mp4';

    function capable(file) {
        return !!(file && w.MP4TrackRemux && typeof w.MP4TrackRemux.replaceAudio === 'function' &&
            w.MP4TrackRemux.looksSupported(file));
    }

    function leadFilter(startSec, rate) {
        var s = +startSec || 0;
        if (Math.abs(s) < 0.001) return '';
        var n = Math.round(Math.abs(s) * (+rate > 0 ? +rate : 48000));
        return s > 0 ? 'adelay=delays=' + n + 'S:all=1' : 'atrim=start_sample=' + n + ',asetpts=PTS-STARTPTS';
    }

    var ALIGN = 'aresample=async=1:first_pts=0';

    /* The alignment also pins the video's own rate when the probe knows it.
       Unpinned, aresample leaves the rate to FFmpeg's negotiation, and a graph
       that mixes in a 48 kHz song resampled a 44.1 kHz video's sound
       (/add-music-to-video, 30.09.2026: the splice gave 48 kHz, the copy
       export — whose [0:a] comes straight from the decoder — 44.1, and the
       stand's soundtracks sat 12.6 dB apart at lag 0; ffmpeg 7 does the same).
       Pinned, the mix reaches the encoder -53 dB from the copy export's. */
    function alignFilter(rate) {
        return +rate > 7999 ? 'aresample=' + Math.round(+rate) + ':async=1:first_pts=0' : ALIGN;
    }
    function startsAligned(s) {
        return /^aresample=(\d+:)?async=1:first_pts=0(?=,|\[|$)/.test(s);
    }

    /* Every [0:a] of a filter_complex reads ONE aligned copy of the video's
       sound (asplit when referenced more than once); the lead goes after the
       graph's last label, [outa] by default. */
    function alignGraph(graph, o) {
        o = o || {};
        var outLabel = o.outLabel || '[outa]';
        var refs = graph.split('[0:a]').length - 1;
        var out = graph;
        if (refs > 0 && !(refs === 1 && graph.indexOf('[0:a]') === 0 && startsAligned(graph.slice(5)))) {
            var legs = [], k = 0;
            out = graph.replace(/\[0:a\]/g, function () { var l = '[sz' + (k++) + ']'; legs.push(l); return l; });
            out = '[0:a]' + alignFilter(o.rate) + (refs > 1 ? ',asplit=' + refs : '') + legs.join('') + ';' + out;
        }
        var lead = leadFilter(o.startSec, o.rate);
        if (lead) {
            var at = out.lastIndexOf(outLabel);
            if (at === out.length - outLabel.length) out = out.slice(0, at) + '[szl];[szl]' + lead + outLabel;
        }
        return out;
    }

    /* An -af chain applied to the video's own sound (input 0). */
    function alignChain(chain, o) {
        o = o || {};
        var c = String(chain || '');
        var head = startsAligned(c) ? '' : alignFilter(o.rate);
        var lead = leadFilter(o.startSec, o.rate);
        return [head, c, lead].filter(Boolean).join(',');
    }

    /* FFmpeg's zero for this file and its sound's rate — one probe per file
       (the probe is cheap, but not free: it shares the instance). */
    var probeCache = typeof WeakMap === 'function' ? new WeakMap() : null;
    async function probe(file) {
        var out = { startSec: 0, rate: 0, channels: 0 };
        if (!file) return out;
        if (probeCache && probeCache.has(file)) return probeCache.get(file);
        try {
            var FP = w.FFmpegProcessor;
            if (FP && typeof FP.probeAudioStream === 'function') {
                var r = await FP.probeAudioStream(file);
                if (r && typeof r.startSec === 'number' && isFinite(r.startSec)) out.startSec = r.startSec;
                if (r && r.sampleRate > 7999) out.rate = r.sampleRate;
                if (r && r.channels > 0) out.channels = r.channels;
            }
        } catch (_) { /* unknown → 0: no lead, the rate the graph states */ }
        if (probeCache) probeCache.set(file, out);
        return out;
    }
    async function startOf(file) { return (await probe(file)).startSec; }

    async function splice(file, soundtrackBlob, opts) {
        opts = opts || {};
        var t0 = Date.now();
        /* The soundtrack is a passenger, never the tool's result: keep the
           blob-URL tracker from counting it as a delivery. Without this a splice
           that FAILED was logged as tool_complete {src:'blob'} 2 s later with the
           soundtrack's size (chat 657, 01.10.2026: two refused exports of a
           5.5 GB video, two «completions» of 71 MB). */
        if (typeof w.__skipBlobAutoTrack === 'function') { try { w.__skipBlobAutoTrack(soundtrackBlob); } catch (_) {} }
        var snd = soundtrackBlob instanceof File ? soundtrackBlob
            : new File([soundtrackBlob], OUT_NAME, { type: 'audio/mp4' });
        var mime = opts.mime || file.type || (/\.mov$/i.test(file.name || '') ? 'video/quicktime' : 'video/mp4');
        // opts.udta — the caller's moov metadata (MP4TrackRemux.replaceAudio): an
        // AI tool's machine-readable mark travels in the moov the splice builds.
        var r = await w.MP4TrackRemux.replaceAudio(file, snd, { mime: mime, udta: opts.udta });
        // co64: chunk-offset tables the output had to widen — a file past 4 GiB
        return {
            blob: r.blob,
            info: { lane: 'zero-copy', ms: Date.now() - t0, parts: r.parts, qt: r.quickTime ? 1 : 0,
                edits: r.edits || 0, flat: r.flattened ? 1 : 0, durable: r.durable ? 1 : 0, udta: r.udta || 'kept',
                co64: r.co64 || 0, desc: (r.described || []).length,
                // the picture's B-frame delay moved into its samples / still behind a cut / kept by a .mov (ms)
                vd: r.videoDelay ? r.videoDelay.zeroMs : 0, vdk: r.videoDelay ? r.videoDelay.keptMs : 0, vdq: r.videoDelay ? r.videoDelay.qtMs : 0 }
        };
    }

    /* ── A soundtrack built on the video's DECODED sound (a dub, a re-voice) ──
       Every decoder hands back the sound track from its first sample, and the
       movie plays that sample MP4TrackRemux.soundStart() after the picture —
       so whatever was built on the decoded timeline must go back that late, in
       the samples (01.10.2026: the /video-dubbing and /redub copy muxes put a
       dub 478 ms early on a track starting 0.478 s in; this pair measured 505
       against the source's 510 ms flash→beep). */
    async function wavRate(wav) {
        try {
            var dv = new DataView(await wav.slice(0, 4096).arrayBuffer()), off = 12;
            if (dv.getUint32(0, false) !== 0x52494646) return 0;
            while (off + 8 <= dv.byteLength) {
                var id = dv.getUint32(off, false), len = dv.getUint32(off + 4, true);
                if (id === 0x666d7420) return dv.getUint32(off + 12, true) || 0;   // 'fmt '
                off += 8 + len + (len & 1);
            }
        } catch (_) {}
        return 0;
    }
    async function movieOf(file) {
        var out = { startSec: 0, durSec: 0 };
        try {
            var ins = await w.MP4TrackRemux.inspect(file);
            out.durSec = ins.durationSec || 0;
            out.startSec = await w.MP4TrackRemux.soundStart(file);
        } catch (_) {}
        return out;
    }
    /* opts: { P: FFmpegProcessor, kbps, mime, udta, race(promise) → promise
       (the caller's watchdog around the render) }. FFmpeg renders ONLY the
       sound — AAC in OUT_NAME, cut at the picture's length (-t: the copy mux
       ran -shortest) — and splice() puts it into the source. Throws what
       splice() throws (.remuxReason = the container is refused). */
    async function spliceDecoded(file, wav, opts) {
        opts = opts || {};
        var m = await movieOf(file), rate = await wavRate(wav);
        var lead = leadFilter(m.startSec, rate || 48000);
        var args = (lead ? ['-af', lead] : []).concat(['-vn', '-c:a', 'aac', '-b:a', (opts.kbps || 192) + 'k'])
            .concat(m.durSec > 0 ? ['-t', m.durSec.toFixed(3)] : []);
        var job = opts.P.processStreaming(wav, args, OUT_NAME);
        var rr = await (typeof opts.race === 'function' ? opts.race(job) : job);
        var snd = (rr && rr.blob) ? rr.blob : rr;
        if (!(snd instanceof Blob)) throw new Error('soundtrack render returned no blob');
        var sp = await splice(file, snd, { mime: opts.mime, udta: opts.udta });
        sp.info.lead_ms = Math.round(m.startSec * 1000);
        return sp;
    }
    /* The classic copy mux's half of the same rule: the WAV itself delayed by
       the sound's start (a new File), or the WAV as it was when the sound
       starts with the picture. */
    async function shiftDecoded(file, wav, P) {
        var s = w.MP4TrackRemux ? await w.MP4TrackRemux.soundStart(file) : 0;
        if (!(Math.abs(s) >= 0.001)) return { wav: wav, lead_ms: 0 };
        var r = await P.processStreaming(wav, ['-af', leadFilter(s, (await wavRate(wav)) || 48000), '-c:a', 'pcm_s16le'], 'dub_shift.wav');
        var b = (r && r.blob) ? r.blob : r;
        if (!(b instanceof Blob)) return { wav: wav, lead_ms: 0 };
        return { wav: new File([b], (wav && wav.name) || 'dub.wav', { type: 'audio/wav' }), lead_ms: Math.round(s * 1000) };
    }

    function copyWallBytes() { return +w.__WASM_OUTPUT_MAX || 1600 * 1048576; }

    /* The classic copy export's ceiling: never past the MEMFS wall, and never
       past what the page's 'video' kind promised before this lane existed
       (device-aware — the layout's __effectiveFileLimit). The classic path was
       never asked to do more than that, and nothing measured says it can: on a
       4 GB phone a 1.5 GB copy lives three times over (MEMFS, readFile, Blob). */
    function classicCeiling() {
        var f = w.__effectiveFileLimit;
        var dev = typeof f === 'function' ? +f('video') : 0;
        return Math.min(copyWallBytes(), dev > 0 ? dev : 1073741824);
    }

    // window., not w.: the inline-script scan of the blades finds our globals by `window.X =`
    window.SoundtrackSplice = {
        OUT_NAME: OUT_NAME,
        capable: capable,
        leadFilter: leadFilter,
        alignFilter: alignFilter,
        alignGraph: alignGraph,
        alignChain: alignChain,
        probe: probe,
        startOf: startOf,
        splice: splice,
        spliceDecoded: spliceDecoded,
        shiftDecoded: shiftDecoded,
        copyWallBytes: copyWallBytes,
        classicCeiling: classicCeiling
    };
})(window);
